import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { query } from './activacions';
import { apiRequest } from './client';
import type {
  PermisDto,
  RolDto,
  UsuariAdminDto,
  UsuariDetallDto,
  UsuariRolDto,
} from './administracio';
import type { Pagina } from './types';

/** Camps que es poden editar al CAI (i que la sincronització global respecta si s'han tocat). */
export const CAMPS_EDITABLES = [
  'nom',
  'cognoms',
  'carrec',
  'departamentId',
  'telefon',
  'idioma',
] as const;
export type CampEditable = (typeof CAMPS_EDITABLES)[number];

export type UsuariPerfilDto = UsuariAdminDto & { campsLocals?: string[] | null };
export type UsuariDetallPerfilDto = Omit<UsuariDetallDto, 'usuari'> & { usuari: UsuariPerfilDto };

export interface EdicioUsuariDto {
  nom: string;
  cognoms?: string | null;
  telefon?: string | null;
  departamentId?: number | null;
  carrec?: string | null;
  idioma?: string | null;
  actiu: boolean;
}

export interface DiferenciaEntraDto {
  camp: string;
  valorCai?: string | null;
  valorEntra?: string | null;
}

export interface PrevisualitzacioSyncDto {
  diferencies: DiferenciaEntraDto[];
}

const invalidaUsuari = (qc: ReturnType<typeof useQueryClient>, id: number) => {
  void qc.invalidateQueries({ queryKey: ['usuaris'] });
  void qc.invalidateQueries({ queryKey: ['etag'] });
  void qc.invalidateQueries({ queryKey: ['usuaris', 'sync-entra', id] });
};

/** PUT /usuaris/{id}: desa els camps editables (el servidor marca com a locals els canviats). */
export function useDesaUsuari(id: number, etag?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cos: EdicioUsuariDto) =>
      apiRequest<UsuariDetallPerfilDto>(`/usuaris/${id}`, {
        method: 'PUT',
        json: cos,
        headers: etag ? { 'If-Match': etag } : undefined,
      }),
    onSuccess: (detall) => {
      qc.setQueryData(['usuaris', 'detall', id], detall);
      invalidaUsuari(qc, id);
    },
  });
}

/** Diferències entre les dades del CAI i les d'Entra ID d'una persona. */
export function usePrevisualitzaSyncUsuari(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ['usuaris', 'sync-entra', id],
    queryFn: () => apiRequest<PrevisualitzacioSyncDto>(`/usuaris/${id}/sync-entra`),
    enabled: enabled && Number.isFinite(id),
    retry: false,
  });
}

/** Sobreescriu amb Entra ID tots els camps o només els indicats (`camps`). */
export function useSyncUsuari(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (camps?: string[]) =>
      apiRequest<UsuariDetallPerfilDto>(`/usuaris/${id}/sync-entra`, {
        method: 'POST',
        json: camps ? { camps } : {},
      }),
    onSuccess: (detall) => {
      if (detall) qc.setQueryData(['usuaris', 'detall', id], detall);
      invalidaUsuari(qc, id);
    },
  });
}

const MIDA_ROL = 500;

/**
 * Persones de cada rol (una consulta per rol, GET /usuaris?rolId=…): permet mostrar els accessos
 * a la llista d'usuaris i comptar persones per rol sense demanar el detall de cada usuari.
 */
export function useUsuarisPerRol(rols: RolDto[]) {
  const consultes = useQueries({
    queries: rols
      .filter((r) => r.id != null)
      .map((r) => ({
        queryKey: ['usuaris', 'perRol', r.id],
        queryFn: () =>
          apiRequest<Pagina<UsuariAdminDto>>(
            `/usuaris${query({ rolId: r.id, page: 0, size: MIDA_ROL })}`,
          ),
        staleTime: 60_000,
      })),
  });
  const rolsAmbId = rols.filter((r) => r.id != null);
  const perUsuari = new Map<number, number[]>();
  const total = new Map<number, number>();
  consultes.forEach((c, i) => {
    const rolId = rolsAmbId[i]?.id;
    if (rolId == null || !c.data) return;
    total.set(rolId, c.data.total);
    for (const u of c.data.items) perUsuari.set(u.id, [...(perUsuari.get(u.id) ?? []), rolId]);
  });
  return {
    perUsuari,
    total,
    carregat: consultes.length > 0 && consultes.every((c) => c.isSuccess),
  };
}

export function usePersonesRol(rolId: number | null, page: number) {
  return useQuery({
    queryKey: ['usuaris', 'admin', { rolId, page }],
    queryFn: () =>
      apiRequest<Pagina<UsuariAdminDto>>(`/usuaris${query({ rolId, page, size: 50 })}`),
    enabled: rolId != null,
  });
}

/** Crea un rol i, si cal, li copia els permisos (Duplica). */
export function useCreaRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ rol, permisos }: { rol: RolDto; permisos?: PermisDto[] }) => {
      const nou = await apiRequest<RolDto>('/rols', { method: 'POST', json: rol });
      if (permisos?.length && nou?.id != null)
        await apiRequest<PermisDto[]>(`/rols/${nou.id}/permisos`, {
          method: 'PUT',
          json: permisos,
        });
      return nou;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rols'] }),
  });
}

/** Assigna diversos àmbits d'un rol de cop (o un de sol). */
export function useAssignaAmbit(usuariId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: UsuariRolDto) =>
      apiRequest<UsuariRolDto>(`/usuaris/${usuariId}/rols`, { method: 'POST', json: r }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['usuaris', 'detall', usuariId] });
      void qc.invalidateQueries({ queryKey: ['usuaris', 'perRol'] });
    },
  });
}

/** Treu una o diverses assignacions (un àmbit o el rol sencer). */
export function useTreuAssignacions(usuariId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      for (const id of ids)
        await apiRequest<void>(`/usuaris/${usuariId}/rols/${id}`, { method: 'DELETE' });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['usuaris', 'detall', usuariId] });
      void qc.invalidateQueries({ queryKey: ['usuaris', 'perRol'] });
    },
  });
}
